import { useEffect, useState, useCallback } from 'react';
import { WALKTHROUGH_EVENTS, HubSpotProperties } from '../../common/constants';
import { useAxeDevtoolsProAnalytics } from '../../common/contexts/analytics';
import { useAuthContext } from '../../common/contexts/auth';
import { analyticsEvent } from '../../common/api-client';

export interface WalkthroughState {
  isMac: boolean;
  completedSteps: number;
  awaitingMessage: boolean;
  onNextClick: () => void;
  onPreviousClick: () => void;
  resetSteps: () => void;
}

const useExtensionWalkthrough = (
  initialCompletedSteps = 0
): WalkthroughState => {
  const analytics = useAxeDevtoolsProAnalytics();
  const { user } = useAuthContext();
  const isMac = navigator.userAgent.includes('Mac');
  const [completedSteps, setCompletedSteps] = useState(initialCompletedSteps);
  const [awaitingMessage, setAwaitingMessage] = useState(true);
  const token = user?.token;

  const onMessage = useCallback(
    (e: MessageEvent) => {
      if (!e.data?.message) {
        return;
      }

      switch (e.data.message) {
        case WALKTHROUGH_EVENTS.opened:
          if (completedSteps === 0) {
            setAwaitingMessage(false);
          }
          break;
        case WALKTHROUGH_EVENTS.scanned:
          if (completedSteps === 1) {
            setAwaitingMessage(false);
          }
          break;
        case WALKTHROUGH_EVENTS.saved:
          if (completedSteps === 2) {
            setAwaitingMessage(false);
          }
          break;
        case WALKTHROUGH_EVENTS.igtCompleted:
          if (completedSteps === 3) {
            setAwaitingMessage(false);
          }
          break;
      }
    },
    [completedSteps]
  );

  const onNextClick = () => {
    setAwaitingMessage(true);

    if (awaitingMessage) {
      analytics.walkthroughStepAlreadyDone({
        currentStep: completedSteps
      });
    } else {
      analytics.walkthroughNext({
        currentStep: completedSteps
      });

      if (token) {
        analyticsEvent(token, HubSpotProperties.AXE_NEXT_WALKTHROUGH_DATE);
      }
    }

    if (completedSteps === 3) {
      analytics.walkthroughComplete();

      if (token) {
        analyticsEvent(token, HubSpotProperties.AXE_COMPLETED_WALKTHROUGH_DATE);
      }
    }

    setCompletedSteps(completedSteps + 1);
  };

  const onPreviousClick = () => {
    setAwaitingMessage(true);
    analytics.walkthroughPrevious({
      currentStep: completedSteps
    });
    setCompletedSteps(completedSteps - 1);
  };

  const resetSteps = () => setCompletedSteps(0);

  useEffect(() => {
    window.addEventListener('message', onMessage);

    // check if devtools is already open
    window.postMessage(
      {
        message: WALKTHROUGH_EVENTS.checkOpen
      },
      '*'
    );

    return () => window.removeEventListener('message', onMessage);
  }, [completedSteps]);

  useEffect(() => {
    analytics.walkthroughStart();

    if (token) {
      analyticsEvent(token, HubSpotProperties.AXE_START_WALKTHROUGH_DATE);
    }
  }, []);

  return {
    isMac,
    completedSteps,
    onNextClick,
    onPreviousClick,
    awaitingMessage,
    resetSteps
  };
};
export default useExtensionWalkthrough;
